import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { TimeField } from '../components/ui/time-field';
import { DatePicker } from '../components/ui/date-picker';
import { Plus, Calendar, Trash2, Pencil, Filter, User } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import LeaveClashDialog from './LeaveClashDialog';

const STATUS_STYLES = {
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  cancelled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

export default function WorkerAvailabilityTab({ api, operators, formatDateDisplay }) {
  const [entries, setEntries] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [editing, setEditing] = useState(null);
  const [filterWorker, setFilterWorker] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [form, setForm] = useState({
    worker_id: '', leave_type_id: '',
    start_date: '', end_date: '',
    is_full_day: true, start_time: '', end_time: '',
    reason: '',
  });
  const [clashDialog, setClashDialog] = useState({ open: false, clashes: [] });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterWorker) params.set('worker_id', filterWorker);
      if (filterStatus) params.set('status', filterStatus);
      const [avRes, ltRes] = await Promise.all([
        api.get(`/worker-availability?${params}`),
        api.get('/leave-types'),
      ]);
      setEntries(avRes.data);
      setLeaveTypes(ltRes.data.filter(lt => lt.is_active));
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api, filterWorker, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.worker_id) { toast.error('Select a worker'); return; }
    if (!form.leave_type_id) { toast.error('Select a leave type'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Start and end dates are required'); return; }
    try {
      const payload = { ...form };
      if (payload.is_full_day) { delete payload.start_time; delete payload.end_time; }
      let res;
      if (editing) {
        res = await api.put(`/worker-availability/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        res = await api.post('/worker-availability', payload);
        toast.success('Leave entry created (auto-approved)');
      }
      const clashes = res.data?.clashing_allocations;
      if (clashes?.length > 0) {
        setClashDialog({ open: true, clashes });
      }
      setDialogOpen(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (entry) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Leave',
      description: 'Delete this leave entry?',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await api.delete(`/worker-availability/${entry.id}`);
          toast.success('Deleted');
          fetchData();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to delete');
        }
      },
    });
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ worker_id: '', leave_type_id: '', start_date: '', end_date: '', is_full_day: true, start_time: '', end_time: '', reason: '' });
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      worker_id: entry.worker_id,
      leave_type_id: entry.leave_type_id,
      start_date: entry.start_date,
      end_date: entry.end_date,
      is_full_day: entry.is_full_day !== false,
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      reason: entry.reason || '',
    });
    setDialogOpen(true);
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  return (
    <div className="space-y-4" data-testid="worker-availability-tab">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filterWorker} onValueChange={setFilterWorker}>
            <SelectTrigger className="w-[200px]" data-testid="filter-worker">
              <SelectValue placeholder="All Workers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Workers</SelectItem>
              {operators.map(op => (
                <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button className="btn-volt" onClick={openCreate} data-testid="add-availability-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Leave
        </Button>
      </div>

      {/* Table */}
      <Card className="card-industrial">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="label-overline">Worker</TableHead>
                <TableHead className="label-overline">Leave Type</TableHead>
                <TableHead className="label-overline">From</TableHead>
                <TableHead className="label-overline">To</TableHead>
                <TableHead className="label-overline">Reason</TableHead>
                <TableHead className="label-overline">Status</TableHead>
                <TableHead className="label-overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(entry => (
                <TableRow key={entry.id} data-testid={`availability-row-${entry.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      {entry.worker_name || 'Unknown'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: entry.leave_type_color || '#71717a' }} />
                      <span className="text-sm">{entry.leave_type_name || 'Unknown'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{formatDateDisplay ? formatDateDisplay(entry.start_date) : entry.start_date}</TableCell>
                  <TableCell className="font-mono text-sm">{formatDateDisplay ? formatDateDisplay(entry.end_date) : entry.end_date}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{entry.reason || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLES[entry.status] || ''}>
                      {entry.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(entry)} data-testid={`edit-avail-${entry.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(entry)} className="text-red-400 hover:text-red-300" data-testid={`delete-avail-${entry.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    No leave entries found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Leave' : 'Add Leave'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Worker <span className="text-red-500">*</span></Label>
              <Select value={form.worker_id} onValueChange={v => setForm({ ...form, worker_id: v })} disabled={!!editing}>
                <SelectTrigger data-testid="avail-worker-select">
                  <SelectValue placeholder="Select worker" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map(op => (
                    <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Leave Type <span className="text-red-500">*</span></Label>
              <Select value={form.leave_type_id} onValueChange={v => setForm({ ...form, leave_type_id: v })}>
                <SelectTrigger data-testid="avail-leave-type-select">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map(lt => (
                    <SelectItem key={lt.id} value={lt.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: lt.color }} />
                        {lt.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-overline">Start Date <span className="text-red-500">*</span></Label>
                <DatePicker
                  value={form.start_date}
                  onChange={d => setForm({ ...form, start_date: d, end_date: (!form.is_full_day || !form.end_date) ? d : form.end_date })}
                  formatDisplay={formatDateDisplay}
                  className="w-full"
                  testId="avail-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">End Date <span className="text-red-500">*</span></Label>
                {!form.is_full_day ? (
                  <Input value={form.end_date || form.start_date} disabled className="w-full opacity-60" data-testid="avail-end-date" />
                ) : (
                  <DatePicker
                    value={form.end_date}
                    onChange={d => setForm({ ...form, end_date: d })}
                    formatDisplay={formatDateDisplay}
                    minDate={form.start_date}
                    className="w-full"
                    testId="avail-end-date"
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="full-day" checked={form.is_full_day} onCheckedChange={v => {
                const updates = { is_full_day: v };
                // Part-day must be single day — lock end_date to start_date
                if (!v && form.start_date) updates.end_date = form.start_date;
                setForm(prev => ({ ...prev, ...updates }));
              }} />
              <Label htmlFor="full-day" className="cursor-pointer text-sm">Full Day</Label>
            </div>
            {!form.is_full_day && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">Start Time</Label>
                  <TimeField value={form.start_time} onChange={v => setForm({ ...form, start_time: v })} />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">End Time</Label>
                  <TimeField value={form.end_time} onChange={v => setForm({ ...form, end_time: v })} />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-overline">Reason</Label>
              <Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Optional notes" rows={2} data-testid="avail-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="btn-volt" onClick={handleSubmit} data-testid="save-availability-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDialog.onConfirm}
      />

      <LeaveClashDialog
        open={clashDialog.open}
        clashes={clashDialog.clashes}
        onClose={() => setClashDialog({ open: false, clashes: [] })}
      />
    </div>
  );
}
